import React, { useState, useEffect } from 'react';
import { ShieldCheck, Zap, BarChart3, Save, Loader2, Globe, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

// --- Custom Light-Themed Power Switch ---
const PowerSwitch = ({ label, description, icon: Icon, enabled, onChange }) => (
  <div className={`group flex items-center justify-between p-5 rounded-2xl border transition-all duration-300 ${
    enabled ? 'bg-indigo-50/50 border-indigo-200' : 'bg-white border-slate-200 hover:border-slate-300'
  }`}>
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-xl transition-colors ${
        enabled ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'
      }`}>
        <Icon size={22} />
      </div>
      <div className="flex flex-col">
        <span className="text-sm font-bold text-slate-800 tracking-tight">{label}</span>
        <span className="text-xs text-slate-500 mt-0.5">{description}</span>
      </div>
    </div>

    <button
      onClick={onChange}
      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
        enabled ? 'bg-indigo-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-sm ${
          enabled ? 'translate-x-8' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

// --- Main Page Component ---
const AdminConfigPage = () => {
  const [features, setFeatures] = useState({
    isSubdomainValidationEnabled: false,
    isAnalyticsEnabled: false,
    isReportsEnabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get('/api/config');
        setFeatures(res.data);
      } catch (err) {
        console.error("Failed to load config", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.post('/api/config/update', { features });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="animate-spin text-indigo-600" size={40} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 p-6 md:p-12 font-sans selection:bg-indigo-100">
      <div className="max-w-2xl mx-auto">
        {/* Header Section */}
        <header className="flex items-center justify-between mb-12">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="flex h-2 w-2 rounded-full bg-green-500"></span>
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">System Infrastructure</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Control Center</h1>
          </div>
          <div className="h-12 w-12 bg-white border border-slate-200 rounded-2xl flex items-center justify-center shadow-sm">
            <Globe className="text-indigo-600" size={24} />
          </div>
        </header>

        {/* Configuration Section */}
        <div className="space-y-4">
          <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Security Configuration</h3>
          
          <PowerSwitch 
            label="Subdomain Integrity Validation"
            description="Forces instant session termination on domain mismatch"
            icon={ShieldCheck}
            enabled={features.isSubdomainValidationEnabled}
            onChange={() => setFeatures({...features, isSubdomainValidationEnabled: !features.isSubdomainValidationEnabled})}
          />

          <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1 mt-10">Application Modules</h3>

          <PowerSwitch 
            label="Real-time Analytics"
            description="Visualize traffic and user behavior across tenants"
            icon={BarChart3}
            enabled={features.isAnalyticsEnabled}
            onChange={() => setFeatures({...features, isAnalyticsEnabled: !features.isAnalyticsEnabled})}
          />

          <PowerSwitch 
            label="Automated Reporting"
            description="Enable PDF/Excel generation for all customers"
            icon={Zap}
            enabled={features.isReportsEnabled}
            onChange={() => setFeatures({...features, isReportsEnabled: !features.isReportsEnabled})}
          />
        </div>

        {/* Action Button */}
        <div className="mt-12">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full relative flex items-center justify-center gap-3 py-4 font-bold rounded-2xl transition-all duration-300 active:scale-[0.98] shadow-md hover:shadow-lg ${
              saved ? 'bg-green-600 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            {saving ? (
              <Loader2 className="animate-spin" size={20} />
            ) : saved ? (
              <>
                <CheckCircle2 size={20} />
                <span>Config Applied Successfully</span>
              </>
            ) : (
              <>
                <Save size={20} />
                <span>Deploy Global Changes</span>
              </>
            )}
          </button>
          
          <p className="text-center text-[11px] text-slate-400 mt-6 font-medium">
            Changes propagate to all **djinfotech.co.in** subdomains within 60 seconds.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminConfigPage;